import Vue from "vue";
import {
  UserAvailability,
  AccountProductFeature,
  FeatureFlags,
  Colors,
  CountryCodes
} from "@/lib/utilities/enums";
import router from "@/router";
import {
  arrayToObj,
  getResponseErrorMessages,
  hashCode
} from "@/lib/utilities";
import CXMApiClient from "../CXMApiClient";
import InventoryApiClient from "@/api/InventoryApiClient.js";

function initialState() {
  return {
    User: {},
    sideNavCollapsed: false,
    IsEmbedded: false, // Used to identify if the app is embedded in another app
    IsMobile: false, // Used to identify if the app is loaded in our Pando mobile apps
    user: {}, //For employee search
    SalesGroups: [],
    currentTime: {
      bySecond: null,
      byMinute: null
    }
  };
}

const state = initialState();

const getters = {
  User: function(state) {
    return state.User;
  },
  user: function(state) {
    // TODO: Replace dependencies with "User" and remove
    return state.User;
  },
  sideNavCollapsed: function(state) {
    return state.sideNavCollapsed;
  },
  IsPluginView(state) {
    // console.log("Router: ", router.history.current);
    return router.history.current.fullPath.indexOf("plugin") >= 0;
    //return location.href.indexOf("plugin") >= 0;
  },
  isAutoAlertUser(state) {
    return state.User && state.User.AccountID != state.User.ViewAccountID;
  },
  hasPermission: state => permission => {
    return state.User && (state.User.Permissions || []).includes(permission);
  },
  isManager: function(state, getters) {
    return getters.hasPermission("pando.cxm.dashboard.manager");
  },
  isBDCManager: function(state, getters) {
    return getters.hasPermission("pando.cxm.dashboard.bdc");
  },
  isSalesPerson: function(state) {
    return true; // Everyone is a salesperson
  },
  isRulesAdmin: function(state, getters) {
    return getters.hasPermission("pando.cxm.rules.admin");
  },
  isReadOnlyAdmin: function(state) {
    return getters.hasPermission("pando.cxm.rules.viewonly");
  },
  SalesGroups: function(state) {
    return state.SalesGroups;
  },
  Environment(state) {
    try {
      let env = "production";
      if (process.env.NODE_ENV == "stage") env = "stage";
      else if (process.env.NODE_ENV == "development") {
        if (location.hostname.startsWith("localhost")) env = "localhost";
        else env = "development";
      }
      return env;
    } catch (err) {
      console.log("Error fetching app environment: ", err);
      return "production";
    }
  },
  InventoryApiClient: function(state, getters) {
    const inventoryApiClient = new InventoryApiClient(
      state.User,
      getters.AuthorizationHeader
    );
    return inventoryApiClient;
  }
};

const actions = {
  handleApiResponseMessage: function(context, message) {
    switch (message.msg.ApiResponseType.toLowerCase()) {
      case "dmspush":
        context.commit("updateDMSPushNotification", message);
    }
  },
  getCurrentUser: function(context) {
    return new Promise((resolve, reject) => {
      Vue.http
        .get("/users/currentUser")
        .then(resp => {
          if (!resp.data || !resp.data.Data || !resp.data.Data.UserId)
            reject(resp);
          else {
            context.commit("gotCurrentUser", resp.data.Data);
            resolve(resp.data.Data);
          }
        })
        .catch(resp => {
          context.commit("gotCurrentUser", null);
          reject(resp);
        });
    });
  },
  clearCurrentUser(context) {
    context.commit("gotCurrentUser", null);
  },
  async setupBaseApp(context) {
    // TODO: Setup other initial stuff
    if (!state.User || !state.User.UserId) return;

    context.dispatch("getHeaderInfo");
    context.dispatch("setupPubNub");
    context.dispatch("initalizeCurrentTime");
    context.dispatch("getGroups");
    context.dispatch("getActivityTypes");
    context.dispatch("getActionTypes");
    // context.dispatch("getActivityAction");
    // context.dispatch("getDeskItStatus");
    context.dispatch("getAlertMinerRoles");
    context.dispatch("getDealershipUsers");
    context.dispatch("loadDealerSettings");
    context.dispatch("getTableauViews");
    // context.dispatch("getLeadSettings");
    // context.dispatch("getTaskTypes");
    context.dispatch("getLookupEnums");
    context.dispatch("getTouchpointResultsEnum");
    context.dispatch("getTemplateCategoryEnums");
    // context.dispatch("getTouchpointResultReasonsEnum");
    // context.dispatch("getContactCulture");
    context.dispatch("getFilterTypes");
    context.dispatch("getTouchpointSettings");
    context.dispatch("getAllPlatformInfo");
    context.dispatch("getCountries");
    context.dispatch("getStateProvinces", CountryCodes.USA);
    context.dispatch("getStateProvinces", CountryCodes.Canada);
    context.dispatch("getAssignmentEditSettings");
    context.dispatch("getCallTrackingSettings");
    // context.dispatch("loadIsCallTrackingEnabled", state.User.ViewSelectedStores.map(m => m.AccountPlatformID));
    context.dispatch("canPushToFinanceMultiStore", {
      accountPlatformIds: state.User.ViewSelectedStores.map(
        m => m.AccountPlatformID
      )
    });
    context.dispatch("canPushToDMSMultiStore", {
      accountPlatformIds: state.User.ViewSelectedStores.map(
        m => m.AccountPlatformID
      )
    });
  },
  toggleEmbeddedMode(context, isEmbedded) {
    context.commit("toggleEmbeddedMode", isEmbedded);
  },
  toggleMobileMode(context, value) {
    context.commit("toggleMobileMode", value);
  },
  checkSuperUser: function(context) {
    return new Promise(async (resolve, reject) => {
      if (
        process.env.NODE_ENV === "production" ||
        process.env.NODE_ENV === "stage"
      ) {
        let client = new CXMApiClient();
        client
          .ProcessRequest("get", "/6B42CA20-8473-41EC-B116-74F59552860A")
          .then(response => {
            return resolve(response.data);
          })
          .catch(error => {
            reject(error);
          });
      } else {
        resolve(true);
      }
    });
  },
  toggleSideNav: function(context, val) {
    context.commit("toggleSideNav", val);
  },
  userUpdated: function(context, updatedUser) {
    if (updatedUser.UserId === context.getters.User.UserId) {
      context.commit("userUpdated", updatedUser);
    }
  },
  handleUserFavoriteResponse: function(context, data) {
    context.commit("handleUserFavoriteResponse", data);
  },
  getGroups(context) {
    return new Promise(async (resolve, reject) => {
      let accountId = context.getters.User.ViewAccountID;
      let apid = context.rootGetters.CXMStores.map(
        m => m.AccountPlatformID
      ).toString();
      let thing = apid.toString();
      resolve(
        await Vue.http
          .post("core/groups/filtergroup", {
            AccountId: accountId,
            PlatformIds: thing,
            GroupTypeIds: "2,4,9",
            Status: true
          })
          .then(data => {
            context.commit("SalesGroups", data.data.Data);
          })
      );
    });
  },
  initalizeCurrentTime(context) {
    const interval = 60 * 1000;

    // Initalize
    (function step() {
      const now = new Date().getTime();
      // Floor to multiple of interval (eg. 3601 -> 3600)
      const flooredNow = Math.floor(now / interval) * interval;

      context.commit("updateCurrentTime", flooredNow);

      // Time to next interval (eg. (3600 + 60) - 3601)
      const diff = flooredNow + interval - now;
      setTimeout(step, diff);
    })();
  }
};

const mutations = {
  gotCurrentUser: function(state, user) {
    if (user) {
      // To support global frame
      user.Id = user.UserId;
      user.Name = user.FirstName + " " + user.LastName;
    }

    state.User = user;
  },
  toggleEmbeddedMode(state, isEmbedded) {
    state.IsEmbedded = isEmbedded;
  },
  toggleMobileMode(state, value) {
    state.IsMobile = value;
  },
  toggleSideNav: function(state, sideNavVal) {
    state.sideNavCollapsed = sideNavVal;
  },
  userUpdated: function(state, updatedUser) {
    if (state.User.UserId === updatedUser.UserId) {
      // console.log("Updated user: ", updatedUser);
      let isFavorite = state.User.IsFavorite;
      for (let prop in updatedUser) {
        try {
          state.User[prop] = updatedUser[prop];
        } catch (err) {
          console.log("Error copying property");
        }
      }
      state.User.IsFavorite = isFavorite;
      state.User.Name = state.User.FirstName + " " + state.User.LastName;
    }
  },
  handleUserFavoriteResponse: function(state, updatedUser) {
    if (state.User.Id === updatedUser.Id) {
      state.User.IsFavorite = updatedUser.IsFavorite;
    }
  },
  updateProductList: function(state, data) {
    state.ProductList = data;
  },
  SalesGroups(state, val) {
    state.SalesGroups = val.sort(function compare(a, b) {
      var aGroupType = a.GroupTypeId;
      var bGroupType = b.GroupTypeId;
      var aName = a.GroupName;
      var bName = b.GroupName;

      if (aGroupType == bGroupType) {
        return aName < bName ? -1 : aName > bName ? 1 : 0;
      } else {
        return aGroupType < bGroupType ? -1 : 1;
      }

      // if (a.GroupName < b.GroupName)
      //   return -1;
      // if (a.GroupName > b.GroupName)
      //   return 1;
      // return 0;
    });
  },
  updateCurrentTime(state, val) {
    state.currentTime.bySecond = val;

    if (val % (60 * 1000) === 0) {
      state.currentTime.byMinute = val;
    }
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};



// WEBPACK FOOTER //
// ./src/store/modules/App.js